/** Gateway route that trades a messaging-signed preferences link token for a Descope token. */
export const PREFERENCES_LINK_EXCHANGE_PATH = '/residents/preferences-link/exchange';

/** Status class only: enough to tell outcomes apart in logs without echoing a response. */
export type HttpStatusClass = '2xx' | '3xx' | '4xx' | '5xx' | 'network';

/**
 * Outcome of `POST /residents/preferences-link/exchange`. Never carries the
 * response body or error beyond the Descope token on success.
 */
export type PreferencesLinkExchangeResult =
  | { kind: 'ok'; token: string; statusClass: HttpStatusClass }
  /** Link signature invalid or expired (401), or a response the contract does not allow. */
  | { kind: 'invalid'; statusClass: HttpStatusClass }
  /** Resident not found, or has no Descope account (404). */
  | { kind: 'notFound'; statusClass: HttpStatusClass }
  /** Rate limited, upstream down, or unreachable — worth one retry after the given wait. */
  | { kind: 'unavailable'; statusClass: HttpStatusClass; retryAfterSeconds: number };

/** Used when the gateway is unreachable or omits a usable `Retry-After`. */
export const DEFAULT_RETRY_AFTER_SECONDS = 1;

const HTTP_STATUS = {
  OK: 200,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  TOO_MANY_REQUESTS: 429,
  SERVER_ERROR_MIN: 500,
} as const;

const MS_PER_SECOND = 1000;

export const NETWORK_UNAVAILABLE: PreferencesLinkExchangeResult = {
  kind: 'unavailable',
  statusClass: 'network',
  retryAfterSeconds: DEFAULT_RETRY_AFTER_SECONDS,
};

const toStatusClass = (status: number): HttpStatusClass => {
  if (status < 300) return '2xx';
  if (status < 400) return '3xx';
  if (status < HTTP_STATUS.SERVER_ERROR_MIN) return '4xx';
  return '5xx';
};

/** `Retry-After` is either delta-seconds or an HTTP-date (RFC 9110 §10.2.3). */
export const parseRetryAfterSeconds = (value: unknown, now: number = Date.now()): number => {
  if (typeof value !== 'string' && typeof value !== 'number') return DEFAULT_RETRY_AFTER_SECONDS;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  const date = Date.parse(String(value));
  if (Number.isNaN(date)) return DEFAULT_RETRY_AFTER_SECONDS;
  return Math.max(0, Math.ceil((date - now) / MS_PER_SECOND));
};

const hasDescopeToken = (data: unknown): data is { token: string } =>
  typeof data === 'object' &&
  data !== null &&
  'token' in data &&
  typeof data.token === 'string' &&
  data.token.length > 0;

interface GatewayResponse {
  status: number;
  data: unknown;
  retryAfter: unknown;
}

/** Maps a gateway response onto the exchange contract. */
export const toPreferencesLinkExchangeResult = ({
  status,
  data,
  retryAfter,
}: GatewayResponse): PreferencesLinkExchangeResult => {
  const statusClass = toStatusClass(status);
  if (status === HTTP_STATUS.OK && hasDescopeToken(data)) return { kind: 'ok', token: data.token, statusClass };
  if (status === HTTP_STATUS.NOT_FOUND) return { kind: 'notFound', statusClass };
  if (status === HTTP_STATUS.TOO_MANY_REQUESTS || status >= HTTP_STATUS.SERVER_ERROR_MIN) {
    return { kind: 'unavailable', statusClass, retryAfterSeconds: parseRetryAfterSeconds(retryAfter) };
  }
  // 401, any other 4xx, and a 200 without a token: retrying the same link cannot help.
  return { kind: 'invalid', statusClass };
};
